import React from "react";
import { Link } from "react-router-dom";
import { TrendUp, TrendDown } from "@phosphor-icons/react";

/**
 * Shared dashboard primitives extracted from /pages/Dashboard.jsx (iter47 refactor).
 * These are pure presentational components used across the SAP-Fiori-styled dashboard.
 */

/* Section header (SAP Fiori "Tile Group" style) */
export function Section({ code, title, subtitle, action, children, testid }) {
  return (
    <section className="mb-9" data-testid={testid}>
      <div className="flex items-end justify-between mb-4 px-0.5">
        <div>
          <div className="section-eyebrow">
            <span className="font-mono-num tracking-[0.22em]">{code}</span>
            <span className="h-px w-8 bg-slate-300/70" />
          </div>
          <h2 className="font-display text-[20px] md:text-[22px] font-semibold text-slate-900 mt-1.5 tracking-[-0.015em]">
            {title}
          </h2>
          {subtitle && <div className="text-[12px] text-slate-500 mt-1 leading-relaxed">{subtitle}</div>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/* KPI Tile (Fiori Horizon Dynamic Tile · refined) */
export function Tile({ label, value, sub, trend, tone = "default", icon: Icon, to, testid, dense }) {
  const tones = {
    default: "bg-white border-slate-200/80 hover:border-slate-300",
    accent:  "bg-[#0F172A] text-white border-[#0F172A]",
    warn:    "bg-white border-amber-200/70 hover:border-amber-300",
    danger:  "bg-white border-red-200/70 hover:border-red-300",
    good:    "bg-white border-emerald-200/70 hover:border-emerald-300",
    orange:  "bg-gradient-to-br from-[#F97316] to-[#EA580C] text-white border-[#EA580C]",
  };
  const iconColor = {
    default: "text-slate-400",
    accent:  "text-[#EA580C]",
    warn:    "text-amber-500",
    danger:  "text-red-500",
    good:    "text-emerald-500",
    orange:  "text-white/80",
  }[tone];
  const labelColor = tone === "accent" || tone === "orange" ? "text-slate-300" : "text-slate-500";
  const subColor = tone === "accent" || tone === "orange" ? "text-slate-300" : "text-slate-500";

  const inner = (
    <div
      className={`group border p-4 rounded-xl shadow-tile transition-all duration-200 ${tones[tone]} ${to ? "cursor-pointer hover:shadow-tile-hover hover:-translate-y-0.5" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className={`text-[10.5px] font-semibold uppercase tracking-[0.16em] ${labelColor}`}>{label}</div>
        {Icon && (
          <div className={`w-7 h-7 rounded-md flex items-center justify-center bg-slate-50 group-hover:bg-white transition-colors ${tone === "accent" ? "bg-white/[0.08] group-hover:bg-white/[0.14]" : ""} ${tone === "orange" ? "bg-white/20 group-hover:bg-white/[0.28]" : ""}`}>
            <Icon size={15} weight="regular" className={iconColor} />
          </div>
        )}
      </div>
      <div className={`font-mono-num font-semibold mt-3 leading-none tracking-[-0.02em] ${dense ? "text-[22px]" : "text-[28px]"}`}>
        {value}
      </div>
      {sub && (
        <div className={`text-[11px] mt-2 flex items-center gap-1.5 ${subColor}`}>
          {trend === "up" && <TrendUp size={11} className="text-emerald-500" />}
          {trend === "down" && <TrendDown size={11} className="text-red-500" />}
          <span>{sub}</span>
        </div>
      )}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

/* Small SAP-style panel */
export function Panel({ title, action, children, testid, className = "" }) {
  return (
    <div className={`bg-white border border-slate-200/80 rounded-xl shadow-tile ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">{title}</div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function MiniRow({ left, mid, right, badge, to, testid }) {
  const inner = (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 group" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-medium text-slate-900 truncate group-hover:text-[#EA580C]">{left}</div>
        {mid && <div className="text-[10.5px] text-slate-500 truncate">{mid}</div>}
      </div>
      <div className="text-[12px] font-mono-num font-semibold text-slate-900 pl-3 whitespace-nowrap flex items-center gap-2">
        {badge}
        {right && <span>{right}</span>}
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

/* Shared chart color palette */
export const CHART = ["#0F172A", "#EA580C", "#0284C7", "#D97706", "#059669", "#7C3AED", "#DB2777"];
